import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { map, switchMap, catchError, distinctUntilChanged } from 'rxjs/operators';
import { LayoutService } from '@app/layout/layout.service';
import { BillingService, PlanFeatures } from './billing.service';

/**
 * Tracks which plan features the current company is entitled to, so the UI can
 * hide/badge gated surfaces. Fail-open: when features are unknown (not loaded or
 * a fetch error), everything is treated as allowed — the backend is the real
 * gate (returns 403 feature_not_available), this is only UX.
 */
@Injectable({ providedIn: 'root' })
export class FeatureAccessService {
  private featuresSubject = new BehaviorSubject<PlanFeatures | null>(null);
  readonly features$ = this.featuresSubject.asObservable();

  constructor(private billing: BillingService, private layout: LayoutService) {
    this.layout.companyId$
      .pipe(
        distinctUntilChanged(),
        switchMap((companyId) => {
          if (companyId == null) return of<PlanFeatures | null>(null);
          return this.billing.getSubscription(companyId).pipe(
            map((s) => s.plan.features),
            catchError(() => of<PlanFeatures | null>(null)),
          );
        }),
      )
      .subscribe((features) => this.featuresSubject.next(features));
  }

  isAllowed(feature: keyof PlanFeatures): boolean {
    const f = this.featuresSubject.value;
    return f == null ? true : !!f[feature];
  }

  isAllowed$(feature: keyof PlanFeatures): Observable<boolean> {
    return this.features$.pipe(map((f) => (f == null ? true : !!f[feature])));
  }

  /** One-shot check against a specific company (for route guards — no stale state). */
  check(companyId: number | string, feature: keyof PlanFeatures): Observable<boolean> {
    return this.billing.getSubscription(companyId).pipe(
      map((s) => !!s.plan.features[feature]),
      catchError(() => of(true)),
    );
  }
}
